import React, { lazy, Suspense } from "react";
import { SEOHead } from "@/components/SEOHead";
import Navbar from "@/components/Navbar";
import LeadForm from "@/components/LeadForm";
import { LazySection } from "@/components/LazySection";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Sparkles, ArrowRight, Home, Users } from "lucide-react";

const Footer = lazy(() => import("@/components/Footer"));

const DCLaunchOffer = () => {
  const scrollToForm = () => {
    const formElement = document.getElementById("lead-form");
    if (formElement) {
      window.dispatchEvent(new Event("force-load-form"));
      formElement.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <div className="min-h-screen bg-background font-body">
      <SEOHead
        title="DC Airbnb Management Launch Offer | JNN Homes"
        description="First 3 months at 10% + free professional photos + free listing audit. Limited to our first 10 Washington DC homes. Full-service Airbnb management for DC homeowners."
        canonical="https://www.jnnhomes.com/dc-launch-offer"
        schemaJson={{
          "@context": "https://schema.org",
          "@type": "Service",
          name: "JNN Homes DC Launch Offer",
          provider: {
            "@type": "LocalBusiness",
            name: "JNN Homes",
            url: "https://www.jnnhomes.com",
            telephone: "+17038905425",
            areaServed: { "@type": "City", name: "Washington, DC" },
          },
          areaServed: "Washington, DC",
          description:
            "Launch offer for Washington DC homeowners: first 3 months at 10% management fee, free professional photos, and free listing audit. Limited to first 10 DC homes.",
        }}
      />
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-16 lg:pt-40 lg:pb-20 bg-gradient-to-br from-background via-background to-muted/50 overflow-hidden">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[400px] opacity-20 pointer-events-none bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-primary/40 via-transparent to-transparent" />
        <div className="container relative mx-auto px-4 text-center max-w-4xl">
          <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-1.5 rounded-full text-sm font-semibold mb-6">
            <Sparkles className="w-4 h-4" />
            Washington DC Launch Offer
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-foreground leading-tight mb-6">
            First 3 months at 10%{" "}
            <span className="text-primary">+ free professional photos</span> +
            free listing audit and optimization.
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto mb-8">
            Limited to our first 10 Washington DC homes. If you own a home in
            the District and you've been thinking about hosting it on Airbnb,
            this is the best time to start.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={scrollToForm}
              className="bg-primary text-primary-foreground hover:bg-primary/90 font-bold text-lg px-8 py-6 h-auto rounded-full"
            >
              Claim Your Spot
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* Lead Form — shown directly, no "Load Form" gate */}
      <section
        id="lead-form"
        className="py-16 bg-muted/20 border-y border-border"
      >
        <div className="container mx-auto px-4 max-w-xl">
          <div className="bg-card rounded-2xl shadow-xl border border-border p-6 md:p-8">
            <LeadForm />
          </div>
          <p className="text-center text-[0.7rem] sm:text-xs text-muted-foreground leading-relaxed max-w-2xl mx-auto px-4 mt-4">
            Offer for new Washington DC owner clients only, limited to the first
            10 homes signed. After 3 months, standard plan pricing (15%, 20% or
            25%) applies.
          </p>
        </div>
      </section>

      {/* Proof strip */}
      <section className="py-12 bg-background">
        <div className="container mx-auto px-4">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-8 text-center">
            <div className="flex items-center gap-3">
              <Home className="w-6 h-6 text-primary" />
              <div className="text-left">
                <p className="text-2xl font-bold text-foreground">10+</p>
                <p className="text-sm text-muted-foreground">Homes managed</p>
              </div>
            </div>
            <div className="hidden sm:block w-px h-12 bg-border" />
            <div className="flex items-center gap-3">
              <Users className="w-6 h-6 text-primary" />
              <div className="text-left">
                <p className="text-2xl font-bold text-foreground">Local team</p>
                <p className="text-sm text-muted-foreground">
                  Based in VA &amp; DC
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Fee clarity — posted plans */}
      <section className="py-16 bg-muted/20 border-t border-border">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-heading font-bold text-foreground mb-3">
              After the launch offer, our standard plans
            </h2>
            <p className="text-muted-foreground">
              No setup fees. Cancel anytime. Performance-aligned fees.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                name: "Essential",
                fee: "15%",
                desc: "Listing optimization, AI pricing, 24/7 guest messaging, check-in/out coordination, review management, calendar management.",
              },
              {
                name: "Full Service",
                fee: "20%",
                desc: "Everything in Essential plus cleaning & turnover, property inspection, inventory restocking, noise monitoring, emergency repair coordination, monthly owner reports.",
                highlight: true,
              },
              {
                name: "Premium Plus",
                fee: "25%",
                desc: "Everything in Tiers 1 & 2 plus dedicated account manager, custom revenue growth plan, professional photography, quarterly strategy calls, multi-property expansion planning.",
              },
            ].map((tier) => (
              <div
                key={tier.name}
                className={`relative rounded-2xl border p-6 flex flex-col ${
                  tier.highlight
                    ? "border-primary bg-card/50 shadow-xl shadow-primary/10 md:scale-105"
                    : "border-border bg-card/30"
                }`}
              >
                {tier.highlight && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground text-xs font-bold px-3 py-1 rounded-full">
                    Most Popular
                  </div>
                )}
                <h3 className="text-xl font-bold text-foreground mb-2">
                  {tier.name}
                </h3>
                <div className="flex items-baseline gap-1 mb-4">
                  <span className="text-4xl font-bold text-primary">
                    {tier.fee}
                  </span>
                </div>
                <p className="text-sm text-muted-foreground flex-grow">
                  {tier.desc}
                </p>
              </div>
            ))}
          </div>
          <p className="text-center text-[0.7rem] sm:text-xs text-muted-foreground leading-relaxed max-w-2xl mx-auto px-4 mt-6">
            Offer for new Washington DC owner clients only, limited to the first
            10 homes signed. After 3 months, standard plan pricing (15%, 20% or
            25%) applies.
          </p>
        </div>
      </section>

      {/* Switching managers? */}
      <section className="py-16 bg-background border-t border-border">
        <div className="container mx-auto px-4 text-center max-w-2xl">
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground mb-4">
            Switching managers?
          </h2>
          <p className="text-muted-foreground mb-6">
            Get a free, no-obligation listing audit. We'll review your current
            Airbnb listing's photos, pricing, description, and guest reviews —
            and show you exactly where you could be earning more.
          </p>
          <Button
            onClick={scrollToForm}
            className="bg-primary text-primary-foreground hover:bg-primary/90 font-bold px-8 py-5 h-auto rounded-full"
          >
            Get My Free Listing Audit
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </section>

      <LazySection
        minHeight="160px"
        rootMargin="200px"
        fallback={<div className="h-40 bg-card/20" />}
      >
        <Suspense fallback={<div className="h-40 bg-card/20" />}>
          <Footer />
        </Suspense>
      </LazySection>
    </div>
  );
};

export default DCLaunchOffer;
